import { Button } from '@fluentui/react-components'
import { Edit24Regular } from '@fluentui/react-icons'
import { useQueryClient } from '@tanstack/react-query'
import React, { useCallback, useState } from 'react'
import 'react-toastify/dist/ReactToastify.css'
import { createRestClient } from '@/contexts/QueryProvider'
import { useGetSearchParam, useSessionStorage } from '@/hooks'
import { ErrorComponent } from '../ErrorComponent'
import { MaintainAppWorkspace } from './MaintainAppWorkspace'
import { Loading } from '../Loading'
import { DirectLinkedUsersPanel } from '../SubscriptionControl/DirectLinkedUsersPanel'
import './WorkspaceDetailsPanel.scss'
import { OverlayDrawerComponent } from '../OverlayDrawerComponent/OverlayDrawerComponent'
import { RestSchemas } from '@/contexts/QueryProvider'
import { isPlatformAdmin, isSuperUser } from '@/util'

type WorkspaceCard = RestSchemas['Messages.Workspaces.WorkspaceCard']

export const WorkspaceDetailsPanel: React.FC = () => {
  const queryClient = useQueryClient()

  // ✅ Ensure `orgId`, `workspaceId`, and `projectName` are properly handled
  const orgName = useGetSearchParam<string>('orgName') ?? 'Unknown Organisation'
  const orgId = useGetSearchParam<number>('orgId') ?? 0 // ✅ Fallback to `0` if undefined
  const workspaceId = useGetSearchParam<number>('subscriptionId') ?? 0 // ✅ Fallback to `0`
  const projectName = useGetSearchParam<string>('projectName') ?? 'Unknown Project'

  const [editDrawerOpen, setDrawerOpen] = useState<'projectTeams' | 'supportTeams' | null>(null)

  const [userRole] = useSessionStorage('roles', 1)
  const [isEditProjectTeamsAllowed] = useState(() => {
    return isPlatformAdmin(userRole) || isSuperUser(userRole)
  })
  const [isEditSupportTeamsAllowed] = useState(() => {
    return isPlatformAdmin(userRole) || isSuperUser(userRole)
  })

  const {
    getSubscriptionsApiGetOrganisationWorkspace,
    getWorkspacesApiGetProjectTeamsWorkspace,
    getWorkspacesApiGetSupportTeamsWorkspace,
  } = createRestClient()

  const {
    data: workspaceRaw,
    isPending: workspacePending,
    isError: workspaceError,
  } = getSubscriptionsApiGetOrganisationWorkspace.useQuery(
    {
      query: { orgId, workspaceId },
    },
    {
      enabled: !!orgId && !!workspaceId,
    }
  )

  const {
    data: projectTeams,
    isPending: projectTeamsPending,
    isError: projectTeamsError,
  } = getWorkspacesApiGetProjectTeamsWorkspace.useQuery(
    {
      query: { workspaceId },
    },
    { enabled: !!workspaceId }
  )

  const {
    data: supportTeams,
    isPending: supportTeamsPending,
    isError: supportTeamsError,
  } = getWorkspacesApiGetSupportTeamsWorkspace.useQuery(
    {
      query: { workspaceId },
    },
    {
      enabled: !!workspaceId,
    }
  )

  const onProjectTeamsSubmit = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['workspaces', 'projectTeam', workspaceId] })
    setDrawerOpen(null)
  }, [queryClient, workspaceId])

  const onSupportTeamsSubmit = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['workspaces', 'supportTeam', workspaceId] })
    setDrawerOpen(null)
  }, [queryClient, workspaceId])

  if (workspacePending) return <Loading />
  if (workspaceError) return <ErrorComponent />

  // ✅ Transform `workspaceRaw` to match `Messages.Workspaces.WorkspaceCard`
  const workspace: WorkspaceCard = {
    workspaceId: workspaceRaw?.workspaceId ?? 0, // ✅ Fallback to `0`
    app_id: workspaceRaw?.applicationId ?? 0, // ✅ Fallback to `0`
    organisation_id: orgId, // ✅ Ensure number
    project_id: workspaceRaw?.projectId ?? 0, // ✅ Fallback to `0`
    app_key: workspaceRaw?.appKey ?? '', // ✅ Ensure string
    application: workspaceRaw?.applicationName ?? 'Unknown Application', // ✅ Ensure string
    organisation: orgName, // ✅ Ensure string
    project: projectName, // ✅ Ensure string
    terms_and_conditions_accepted: true, // ✅ Default to `true`
    terms_and_conditions_url: '', // ✅ Placeholder
    privacy_statement_url: '', // ✅ Placeholder
    status: workspaceRaw?.isFrozen ? 'Frozen' : 'Active', // ✅ Ensure valid value
    appTypes: (workspaceRaw?.appTypes as 0 | 1 | 2 | 4) ?? 0, // ✅ Ensure type safety
    card: {
      title: workspaceRaw?.applicationName ?? 'Unknown Application',
      subtitle: `Project: ${projectName}`,
      image: '', // ✅ Placeholder
    },
  }

  return (
    <>
      <div className="flex flex-col gap-y-5 py-4">
        <div className="w-full space-y-4">
          <div className="flex w-full flex-col gap-y-4 rounded bg-white p-6 pt-4">
            <h6 className="border-b">Workspace details</h6>
            <div className="flex shrink-0 flex-row flex-wrap gap-x-20 gap-y-4">
              {[
                { label: 'Organisation', value: orgName },
                { label: 'Project', value: projectName },
                { label: 'Application', value: workspace.application },
              ].map(({ label, value }) => (
                <div className="flex flex-col whitespace-nowrap" key={label}>
                  <span className="text-sm font-light">{label}:</span>
                  <span className="text-base">{value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Project Teams */}
          <div className="flex w-full flex-col gap-y-4 rounded py-5">
            <div className="flex flex-row justify-between gap-2 border-b">
              <h6>Project teams</h6>
              <Button
                disabled={!isEditProjectTeamsAllowed}
                onClick={() => setDrawerOpen('projectTeams')}
                shape="square"
                appearance="subtle"
                size="small"
                icon={<Edit24Regular />}
              />
            </div>
            <div className="flex flex-row flex-wrap gap-x-2 whitespace-nowrap text-base">
              {projectTeamsPending ? (
                <Loading />
              ) : projectTeamsError ? (
                <ErrorComponent />
              ) : projectTeams.length === 0 ? (
                '(No project teams found)'
              ) : (
                projectTeams.map((team, i) => (
                  <span key={team.id}>
                    {team.name}
                    {i < projectTeams.length - 1 ? ',' : ''}
                  </span>
                ))
              )}
            </div>
          </div>

          {/* Support Teams */}
          <div className="flex w-full flex-col gap-y-4 rounded py-5">
            <div className="flex flex-row justify-between gap-2 border-b">
              <h6>Support teams</h6>
              <Button
                disabled={!isEditSupportTeamsAllowed}
                onClick={() => setDrawerOpen('supportTeams')}
                shape="square"
                appearance="subtle"
                size="small"
                icon={<Edit24Regular />}
              />
            </div>
            <div className="flex flex-row flex-wrap gap-x-2 whitespace-nowrap text-base">
              {supportTeamsPending ? (
                <Loading />
              ) : supportTeamsError ? (
                <ErrorComponent />
              ) : supportTeams.length === 0 ? (
                '(No support teams found)'
              ) : (
                supportTeams.map((team, i) => (
                  <span key={team.id}>
                    {team.name}
                    {i < supportTeams.length - 1 ? ',' : ''}
                  </span>
                ))
              )}
            </div>
          </div>
        </div>
        {workspace.project_id && workspace.app_id && workspace.organisation_id && (
          <DirectLinkedUsersPanel
            workspaceId={workspace.workspaceId}
            projectId={workspace.project_id}
            appId={workspace.app_id}
            orgId={workspace.organisation_id}
          />
        )}
      </div>

      <OverlayDrawerComponent
        open={editDrawerOpen === 'projectTeams'}
        size="medium"
        setOpen={(isOpen) => setDrawerOpen(isOpen ? 'projectTeams' : null)}
        title={'Manage Project Teams'}
        content={<MaintainAppWorkspace onSubmit={onProjectTeamsSubmit} selectedWorkspace={workspace} type="project" />}
      />

      <OverlayDrawerComponent
        open={editDrawerOpen === 'supportTeams'}
        size="medium"
        setOpen={(isOpen) => setDrawerOpen(isOpen ? 'supportTeams' : null)}
        title={'Manage Support Teams'}
        content={<MaintainAppWorkspace onSubmit={onSupportTeamsSubmit} selectedWorkspace={workspace} type="support" />}
      />
    </>
  )
}
